import React, { FC } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import isPast from "date-fns/isPast";
import parse from "date-fns/parse";
import { LogInFormPersonType } from "@Shape-Digital/kudzu-data/lib/types/actions";
import Dialog from "../../Unknown/Dialog";
import DialogTitle from "../../Unknown/DialogTitle";
import DialogContent from "../../Unknown/DialogContent";
import DialogActions from "../../Unknown/DialogActions";
import Button from "../../Unknown/Button";
import Grid from "../../Unknown/Grid";
import Box from "../../Unknown/Box";
import FormikTextField from "../../Unknown/FormikTextField";
import Checkbox from "../../Unknown/Checkbox";
import FormControlLabel from "../../Unknown/FormControlLabel";
import { CHECKOUT_DATE_FORMAT } from "../AppointmentCheckoutFormSectionWrapper/getValidationSchema";
import useTranslations from "./useTranslations";

type AddPatientValues = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  isLegalGuardian: boolean;
  isFamilyMember: boolean;
};

type AppointmentCheckoutAddPatientModalProps = {
  isOpen: boolean;
  personType: LogInFormPersonType;
  onClose: () => void;
  onSave: (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    isLegalGuardian: boolean,
    isFamilyMember: boolean,
  ) => Promise<void>;
};

const getInitialValues = (
  personType: LogInFormPersonType,
): AddPatientValues => ({
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  isLegalGuardian: personType === "legalGuardian",
  isFamilyMember:
    personType === "legalGuardian" || personType === "familyMember",
});

const AppointmentCheckoutAddPatientModal: FC<
  AppointmentCheckoutAddPatientModalProps
> = ({ isOpen, personType, onClose, onSave }) => {
  const translations = useTranslations();

  const validationSchema = Yup.object({
    firstName: Yup.string().trim().required(translations.requiredField),
    lastName: Yup.string().trim().required(translations.requiredField),
    dateOfBirth: Yup.string()
      .required(translations.requiredField)
      .test("valid-past-date", translations.dateIsInvalid, (value) => {
        if (!value) return false;
        const date = parse(value, CHECKOUT_DATE_FORMAT, Date.now());
        return isPast(date);
      }),
  });

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: { width: 800, maxWidth: "100%" } }}
    >
      <Formik
        initialValues={getInitialValues(personType)}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSave(
            values.firstName.trim(),
            values.lastName.trim(),
            values.dateOfBirth,
            values.isLegalGuardian,
            values.isFamilyMember,
          );
        }}
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogTitle>{translations.addPatientTitle}</DialogTitle>
            <DialogContent>
              <Box py={3}>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      required
                      fullWidth
                      autoComplete="off"
                      name="firstName"
                      label={translations.firstNameLabel}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      required
                      fullWidth
                      autoComplete="off"
                      name="lastName"
                      label={translations.lastNameLabel}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      required
                      fullWidth
                      type="date"
                      name="dateOfBirth"
                      label={translations.dateOfBirthLabel}
                      InputLabelProps={{ shrink: true }}
                      helperText={translations.dateOfBirthHelper}
                      placeholder={translations.dateOfBirthPlaceholder}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" flexDirection="column">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isLegalGuardian}
                            onChange={(e) => {
                              setFieldValue(
                                "isLegalGuardian",
                                e.target.checked,
                              );
                              if (e.target.checked) {
                                setFieldValue("isFamilyMember", true);
                              }
                            }}
                            disabled={isSubmitting}
                          />
                        }
                        label={translations.isLegalGuardianLabel}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isFamilyMember}
                            onChange={(e) => {
                              setFieldValue("isFamilyMember", e.target.checked);
                              if (!e.target.checked) {
                                setFieldValue("isLegalGuardian", false);
                              }
                            }}
                            disabled={isSubmitting}
                          />
                        }
                        label={translations.isFamilyMemberLabel}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                {translations.cancelButton}
              </Button>
              <Button onClick={submitForm} isLoading={isSubmitting}>
                {translations.saveButton}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AppointmentCheckoutAddPatientModal;
